import React, { useState, useEffect } from 'react';
import { Button, Input, Select, Card, Statistic, Spin, Empty, Tag, Space, Modal, message, Tooltip } from 'antd';
import { 
  ReloadOutlined, SearchOutlined, InboxOutlined, 
  CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined,
  CalendarOutlined, UserOutlined, PhoneOutlined, MailOutlined,
  EnvironmentOutlined, VideoCameraOutlined, FileTextOutlined
} from '@ant-design/icons';
import InvestorRequestTable from '../components/admin/investor/InvestorRequestTable';
import InvestorRequestDetailModal from '../components/admin/investor/InvestorRequestDetailModal';
import investorApi from '../api/investorApi';

const { Option } = Select;

const InvestorRequestManagement = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [requestTypeFilter, setRequestTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const loadRequests = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (requestTypeFilter !== 'all') filters.requestType = requestTypeFilter;
      if (statusFilter !== 'all') filters.status = statusFilter;

      const response = await investorApi.getAllRequests(filters);
      
      if (response && response.success) {
        const requestsData = response.data || [];
        setRequests(Array.isArray(requestsData) ? requestsData : []);
      } else {
        console.error('Failed to fetch requests - invalid response');
        setRequests([]);
        message.error('Failed to fetch investor requests');
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
      setRequests([]);
      message.error('Error loading investor requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, [requestTypeFilter, statusFilter]);

  const openDetailModal = (request) => {
    setSelectedRequest(request);
    setIsDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedRequest(null);
  };

  const handleStatusUpdate = async (requestId, newStatus, notes) => {
    try {
      const response = await investorApi.updateRequestStatus(requestId, { status: newStatus, notes });
      
      if (response && response.success) {
        message.success('Request status updated successfully');
        await loadRequests();
        closeDetailModal();
      } else {
        message.error('Failed to update request status');
      }
    } catch (error) {
      console.error('Error updating request status:', error);
      message.error('Error updating request status');
    }
  };

  const handleDeleteRequest = async (requestId) => {
    Modal.confirm({
      title: 'Delete Request',
      content: 'Are you sure you want to delete this request? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          // Note: You'll need to add a delete endpoint in your API
          message.success('Request deleted successfully');
          await loadRequests();
        } catch (error) {
          console.error('Error deleting request:', error);
          message.error('Error deleting request');
        }
      },
    });
  };

  const filteredRequests = Array.isArray(requests) 
    ? requests.filter(request => {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
          request.fullName?.toLowerCase().includes(searchLower) ||
          request.authorizedName?.toLowerCase().includes(searchLower) ||
          request.email?.toLowerCase().includes(searchLower) ||
          request.company?.toLowerCase().includes(searchLower) ||
          request.companyName?.toLowerCase().includes(searchLower) ||
          request.phone?.includes(searchTerm);
        return matchesSearch;
      })
    : [];

  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    rejected: requests.filter(r => r.status === 'rejected').length,
    completed: requests.filter(r => r.status === 'completed').length,
    siteVisit: requests.filter(r => r.requestType === 'site-visit').length,
    virtualMeeting: requests.filter(r => r.requestType === 'virtual-meeting').length,
  };

  return (
    <>
      <div className="max-w-9xl mx-auto w-full" style={{ fontFamily: 'Rubik, sans-serif' }}>
        <div className="p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Rubik, sans-serif', color: '#470172' }}>
                Investor Requests
              </h1>
              <p className="text-gray-600" style={{ fontFamily: 'Rubik, sans-serif' }}>
                Manage site visits and virtual meetings
              </p>
            </div>
            <Button
              icon={<ReloadOutlined spin={loading} />}
              onClick={loadRequests}
              disabled={loading}
              size="large"
              style={{
                fontFamily: 'Rubik, sans-serif',
                fontSize: '16px',
                fontWeight: 400,
                color: '#470172',
                borderColor: '#470172',
                height: 'auto',
                padding: '10px 20px'
              }}
            >
              Refresh
            </Button>
          </div>

          {/* Search and Filters */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Input
                size="large"
                placeholder="Search by name, email, company..."
                prefix={<SearchOutlined style={{ color: '#470172' }} />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  fontFamily: 'Rubik, sans-serif',
                  fontSize: '16px',
                  fontWeight: 400,
                }}
              />
            </div>
            <div>
              <Select
                size="large"
                value={requestTypeFilter}
                onChange={(value) => setRequestTypeFilter(value)}
                style={{
                  width: '100%',
                  fontFamily: 'Rubik, sans-serif',
                  fontSize: '16px',
                  fontWeight: 400,
                }}
              >
                <Option value="all">All Request Types</Option>
                <Option value="site-visit">Site Visits</Option>
                <Option value="virtual-meeting">Virtual Meetings</Option>
              </Select>
            </div>
            <div>
              <Select
                size="large"
                value={statusFilter}
                onChange={(value) => setStatusFilter(value)}
                style={{
                  width: '100%',
                  fontFamily: 'Rubik, sans-serif',
                  fontSize: '16px',
                  fontWeight: 400,
                }}
              >
                <Option value="all">All Status</Option>
                <Option value="pending">Pending</Option>
                <Option value="approved">Approved</Option>
                <Option value="rejected">Rejected</Option>
                <Option value="completed">Completed</Option>
              </Select>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6">
            <Card>
              <Statistic
                title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Total Requests</span>}
                value={stats.total}
                prefix={<InboxOutlined style={{ color: '#470172' }} />}
                valueStyle={{ fontFamily: 'Rubik', fontSize: '28px', fontWeight: 400, color: '#470172' }}
              />
            </Card>
            <Card>
              <Statistic
                title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Pending</span>}
                value={stats.pending}
                prefix={<ClockCircleOutlined style={{ color: '#fa8c16' }} />}
                valueStyle={{ fontFamily: 'Rubik', fontSize: '28px', fontWeight: 400, color: '#fa8c16' }}
              />
            </Card>
            <Card>
              <Statistic
                title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Approved</span>}
                value={stats.approved}
                prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ fontFamily: 'Rubik', fontSize: '28px', fontWeight: 400, color: '#52c41a' }}
              />
            </Card>
            <Card>
              <Statistic
                title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Completed</span>}
                value={stats.completed}
                prefix={<CheckCircleOutlined style={{ color: '#1890ff' }} />}
                valueStyle={{ fontFamily: 'Rubik', fontSize: '28px', fontWeight: 400, color: '#1890ff' }}
              />
            </Card>
          </div>

          {/* Request Type Stats */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 mb-6">
            <Card>
              <Statistic
                title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Site Visits</span>}
                value={stats.siteVisit}
                prefix={<EnvironmentOutlined style={{ color: '#470172' }} />}
                valueStyle={{ fontFamily: 'Rubik', fontSize: '28px', fontWeight: 400, color: '#470172' }}
              />
            </Card>
            <Card>
              <Statistic
                title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Virtual Meetings</span>}
                value={stats.virtualMeeting}
                prefix={<VideoCameraOutlined style={{ color: '#470172' }} />}
                valueStyle={{ fontFamily: 'Rubik', fontSize: '28px', fontWeight: 400, color: '#470172' }}
              />
            </Card>
          </div>

          {/* Requests Table */}
          <Card
            styles={{ body: { overflowX: 'auto' } }}
            title={
              <div>
                <p style={{ fontFamily: 'Rubik, sans-serif', fontSize: '15px', fontWeight: 400, color: '#470172', margin: '4px 0 0 0', opacity: 0.7 }}>
                  List of all investor requests
                </p>
              </div>
            }
          >
            {loading ? (
              <div className="flex justify-center items-center p-8">
                <Spin size="large" />
              </div>
            ) : filteredRequests.length > 0 ? (
              <InvestorRequestTable
                requests={filteredRequests}
                onView={openDetailModal}
                onStatusUpdate={handleStatusUpdate}
              />
            ) : (
              <Empty
                description={
                  <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>
                    {searchTerm || requestTypeFilter !== 'all' || statusFilter !== 'all' 
                      ? 'No requests found matching your filters' 
                      : 'No investor requests yet'}
                  </span>
                }
              />
            )}
          </Card>
        </div>
      </div>

      {isDetailModalOpen && selectedRequest && (
        <InvestorRequestDetailModal
          request={selectedRequest}
          isOpen={isDetailModalOpen}
          onClose={closeDetailModal}
          onStatusUpdate={handleStatusUpdate}
        />
      )}
    </>
  );
};

export default InvestorRequestManagement;
